import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NrToggleSwitchComponent } from 'src/app/shared/components/nr-toggle-switch/nr-toggle-switch.component';
import { FormControl } from '@angular/forms';
import { NgbPopoverModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ThresholdItem } from 'src/app/modules/cat/interfaces/cat-settings.interface';
import { DeviceService, ResponsiveState } from 'src/app/services/shared/device.service';
import { SubHeaderPopoverService } from '@services/shared/sub-header-popover.service';
import { Popover, PopoverModule } from 'primeng/popover';
import { UserAuthService } from '@services/user-auth.service';
import { ICON_NAMES } from '@modules/cat/constants';
import { NRIconComponent } from '@shared/components/nr-icon/nr-icon.component';
import { TooltipModule } from 'primeng/tooltip';
import { CatService } from '@modules/cat/services/cat.service';
import { WebSocketService } from '@services/shared/web-socket.service';
import { BannerService } from '@shared/components/nr-banner/banner.service';
import { Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { SidenavComponent } from '@components/sidenav/sidenav.component';
import { CatCounterService } from '@services/cat-counter.service';
import { RegionRouteService } from '@services/shared/nr-region-route.service';

@Component({
  selector: 'app-sub-header',
  standalone: true,
  templateUrl: './sub-header.component.html',
  styleUrls: ['./sub-header.component.scss'],
  imports: [
    CommonModule,
    NrToggleSwitchComponent,
    NgbTooltipModule,
    NgbPopoverModule,
    PopoverModule,
    NRIconComponent,
    TooltipModule,
    SidenavComponent
  ],
})
export class SubHeaderComponent implements OnInit, OnDestroy {
  bus = inject(SubHeaderPopoverService);
  private readonly userService = inject(UserAuthService);

  private readonly webSocketService = inject(WebSocketService);
  private readonly bannerService = inject(BannerService);
  private readonly destroy$ = new Subject<void>();
  private catCountSub: Subscription | null = null;
  latestModelIssueTime: any;
  isCatPlaybackActive = false;
  isHistoryMode = false;
  showBanner: boolean = true;
  catIssueDelayMessage = '';

  @Input() pageTitle = '';
  @Input() showHistoryToggle = false;
  @Input() showPDFDownload = false;
  @Input() showEmailSubscription = false;
  @Input() emailSubscription = false;
  @Input() showRagIcon = false;
  @Input() historyMode = false;
  @Input() showAudioIcon = false;
  @Input() showPdfIcon = false;
  @Input() showSubscribeIcon = false;
  @Input() audioTooltip = '';
  @Input() isSubscribedId = false;
  @Output() historyToggleClicked = new EventEmitter<void>();
  @Output() pdfDownloadClicked = new EventEmitter<void>();
  @Output() emailSubscriptionClicked = new EventEmitter<void>();
  @Output() ragIconClicked = new EventEmitter<void>();
  @Output() audioAlertChanged = new EventEmitter<boolean>();
  @Output() subscribeClicked = new EventEmitter<void>();
  @ViewChild('pop') pop!: Popover;
  @ViewChild('triggerRef', { static: false }) triggerRef!: ElementRef;

  // history toggle
  formControl: FormControl = new FormControl(false);
  showToggle = false;
  enablePDFDownload = false;
  featureIcons: { imagePath: string }[] = [];
  headers: string[] = ['Category'];
  rows: ThresholdItem[] = [];
  responsiveState!: ResponsiveState;
  popoverVisible = false;
  isThresholdVisible = false;
  icons = ICON_NAMES;
  audioPlayed = false;
  isCAT = false;
  audioSubscription: Subscription | null = null;
  isSubscribedToAudioAlerts: boolean | null = null;
  latetIssueTime: any;
  newModelRunIssue: Subscription | null = null;
  userRouteId: number | null = null;
  pendingAudioIssueTimestamp: string | null = null;
  pendingAudioRetryHandler: (() => void) | null = null;
  currentAudioInstanceId: number = 0;
  lastProcessedAudioTimestamp: string | null = null;

  private readonly catService = inject(CatService);
  private readonly subHeaderPopoverService = inject(SubHeaderPopoverService);
  private readonly router = inject(Router);
  private readonly catCounterService = inject(CatCounterService);
  private readonly regionRouteService = inject(RegionRouteService);

  constructor(private readonly deviceService: DeviceService) {
    this.deviceService.responsiveState$.subscribe((state) => {
      this.responsiveState = state;
    });
  }

  ngOnInit() {
    this.getAudioSubscriptions();
  }

  getAudioSubscriptions() {
    const currentUrl = this.router.url;
    this.isCAT = currentUrl.includes('/cat');
    this.audioSubscription = this.userService.userProfileData$.pipe(takeUntil(this.destroy$)).subscribe((userProfile) => {
      if (userProfile) {
        this.userRouteId = userProfile.routeId;
        this.regionRouteService.setSelectedRouteIds([this.userRouteId]);
        const isSubscribed = userProfile?.alertPreference?.categories?.[0]?.preferences?.find(
          (pref) => pref.alertDescription === 'Audio Alerts',
        )?.isActive;

        this.isSubscribedToAudioAlerts = isSubscribed;
        this.catService.setIsSubscribedToAudioAlerts(isSubscribed);
        this.catService.getAudioAlertWithNewModelRun();

        if (this.userRouteId && this.userRouteId !== 0 && !this.catCountSub) {
          this.getCATCounter();
        } else if (!this.userRouteId || this.userRouteId === 0) {
          console.log('SubHeaderComponent: Skipping CAT counter subscription for invalid routeId');
        }
      }
      });
  }
  
  getAudioAlertWithNewModelRun() {
    // avoid creating multiple simultaneous subscriptions
    if (this.newModelRunIssue) {
      this.newModelRunIssue.unsubscribe();
      this.newModelRunIssue = null;
    }

    this.newModelRunIssue = combineLatest([
      this.catService.containsNewAlertCycle$,
      this.catService.catNewModelRunIssue$,
      this.catService.isLatestSelected$,
      this.catService.extremeAlert$,
    ])
     .pipe(
    //  Do not emit if the issue/timestamp is missing or null.
    filter(([hasNewCycle, issue, isLatest, extremeIssue]) => !!issue && extremeIssue),
          // Compare by individual values instead of JSON.stringify
      distinctUntilChanged(
        ([prevCycle, prevIssue, prevLatest, prevExtreme],
         [currCycle, currIssue, currLatest, currExtreme]) =>
          prevCycle === currCycle &&
          prevIssue === currIssue &&
          prevLatest === currLatest &&
          prevExtreme === currExtreme
      ),
      takeUntil(this.destroy$),
    ).subscribe(([hasNewCycle, issue, isLatest, extremeIssue]) => {
        console.log('Received new model run issue update:', { hasNewCycle, issue, isLatest, extremeIssue });
        const currentUrl = this.router.url;
        this.isCAT = currentUrl.includes('/cat');
        // If 'issue' is null, generate a fallback key based on the cycle status.
        // This will stay consistent as long as the state doesn't change, preventing duplicates.
        const currentIssueTimestamp: string = issue;
        // const currentIssueTimestamp: string | null = this.catService.getIssueTimestamp(issue);
        console.log('Is user subscribed to audio alerts?', this.isSubscribedToAudioAlerts);
        console.log('Is CAT history mode?', this.historyMode);
        console.log('Is user on CAT page?', this.isCAT);
        console.log('Does the issue contain a new alert cycle?', hasNewCycle);
        console.log('Current issue timestamp:', currentIssueTimestamp);
        console.log('Is the latest issue selected?', isLatest);
        console.log('Extreme issue from CAT service:', extremeIssue);
        console.log('Last processed audio timestamp:', this.lastProcessedAudioTimestamp);
        
        if (this.isSubscribedToAudioAlerts &&
          !this.historyMode &&
          this.isCAT &&
          hasNewCycle &&
          currentIssueTimestamp &&
          isLatest && 
          extremeIssue) {
            // It checks if it's new, and automatically marks it as read so it can't duplicate.
            const isNewAlert = this.catService.shouldPlayAudioForIssue(currentIssueTimestamp);
            if (isNewAlert) {
              this.lastProcessedAudioTimestamp = currentIssueTimestamp;
              this.playAudioAlert(currentIssueTimestamp);
            } else {
              console.log('Audio alert already processed by CAT service, skipping playback.');
            }
          } else {
            console.log("Audio alert conditions not met, skipping playback. Conditions:", {
              isSubscribed: this.isSubscribedToAudioAlerts,
              historyMode: !this.historyMode,
              isCAT: this.isCAT,
              hasNewCycle,
              currentIssueTimestamp,
              isLatest,
              extremeIssue
            });
        }
      });
  } 

  getCATCounter(): void {
    this.catCountSub?.unsubscribe();
    this.catCountSub = this.catService.catNewModelRunIssue$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$),
      )
      .subscribe((issue) => {
        if (!issue || !this.userRouteId) {
          return;
        }

        this.catCounterService.refreshCatCount([this.userRouteId], this.isCAT);
      });
  }

  toggleClick(): void {
    this.historyToggleClicked.emit(this.formControl.value);
  }

  onPDFDownloadClick(): void {
    // Placeholder for PDF download functionality
  }

  /**
   * Displays the popover when the info icon is triggered via mouse event.
   * Only activates on non-touch (desktop) devices.
   * Anchors the popover to the trigger element using the mouse event for positioning.
   */
  onInfoIconTrigger(event: Event): void {
    if (!this.isTouchDevice() && this.triggerRef?.nativeElement) {
      this.pop.show(event, this.triggerRef.nativeElement);
    }
  }

  /**
   * Closes the popover on desktop devices when the info icon loses focus or is dismissed.
   * Ensures the popover is hidden and resets the visibility flag.
   */
  onInfoIconClose(): void {
    if (!this.isTouchDevice()) {
      this.pop.hide();
      this.popoverVisible = false;
    }
  }

  /**
   * Toggles the popover visibility on touch devices (mobile/tablet).
   * Uses internal visibility state to show or hide the popover.
   * Anchors the popover to the trigger element without relying on mouse events.
   */
  onInfoIconToggle(): void {
    if (this.isTouchDevice() && this.triggerRef?.nativeElement) {
      if (this.popoverVisible) {
        this.pop.hide();
        this.popoverVisible = false;
      } else {
        this.pop.show(undefined, this.triggerRef.nativeElement);
        this.popoverVisible = true;
      }
    }
  }

  /**
   * Detects if the current device supports touch input.
   * Returns true for mobile and tablet devices.
   */
  isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  toggleAudioAlerts(): void {
    this.isSubscribedToAudioAlerts = !this.isSubscribedToAudioAlerts;
    this.audioAlertChanged.emit(this.isSubscribedToAudioAlerts);
    // Reset the last processed timestamp when toggling audio alerts to allow fresh audio playback
    this.lastProcessedAudioTimestamp = null;
    this.catService.getAudioAlertWithNewModelRun();
  }

  cancelPendingAudioRetry(): void {
    if (this.pendingAudioRetryHandler) {
      document.removeEventListener('click', this.pendingAudioRetryHandler);
      document.removeEventListener('touchstart', this.pendingAudioRetryHandler);
      document.removeEventListener('keydown', this.pendingAudioRetryHandler);
      this.pendingAudioRetryHandler = null;
    }
    this.pendingAudioIssueTimestamp = null;
    this.currentAudioInstanceId++;
  }

  playAudioAlert(issueTimestamp: string): void {
    this.cancelPendingAudioRetry();
    const currentInstanceId = this.currentAudioInstanceId;
    this.pendingAudioIssueTimestamp = issueTimestamp;
    const audio = new Audio('assets/audio/CAT-audio-ding-ding-alert.mp3');
    audio.load();
    audio
      .play()
      .then(() => {
        if (currentInstanceId !== this.currentAudioInstanceId) {
          console.warn('Audio instance mismatch - ignoring stale audio playback');
          return;
        }
        console.log("playing audio alert");
        this.catService.markAudioAsPlayed(issueTimestamp);
        this.pendingAudioIssueTimestamp = null;
        setTimeout(() => {
          audio.pause();
          audio.currentTime = 0;
        }, 5000);
      })
      .catch((error) => {
        const failureSystemTime = new Date().toLocaleString();
        console.log('error', error);
        if (error.name === 'NotAllowedError') {
          console.warn(
            `[Audio Blocked] Browser blocked autoplay at ${failureSystemTime} for model run issue: "${issueTimestamp}". ` +
            `The user has not interacted with the page yet.`
          );        
        } else {
          console.error('Audio playback failed:', error);
          this.pendingAudioIssueTimestamp = null;
        }
      });
  }

  pdfDownload() {
    this.pdfDownloadClicked.emit();
  }

  onSubscribeClick() {
    this.subscribeClicked.emit();
  }

  onPopoverOpened(): void {
    // ng-bootstrap injects the popover content into container="body"
    const popoverEl = document.querySelector('.popover.show .popover-focus-root') as HTMLElement;

    if (popoverEl) {
      // Find the first focusable element
      const focusable = popoverEl.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusable) {
        focusable.focus();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelPendingAudioRetry();
    this.audioSubscription?.unsubscribe();
    this.newModelRunIssue?.unsubscribe();
    this.catCountSub?.unsubscribe();
  }
  
}
